<template>
  <section class="bg-[#fef9f6] to-white min-h-screen">
  <div class="max-w-4xl mx-auto px-6 py-20">

    <!-- Heading -->
    <div class="text-center mb-16">
      <h1
        class="text-3xl sm:text-4xl md:text-5xl
               font-extrabold font-serif tracking-tight
               text-[#924f19] mb-4"
      >
        Dental FAQs – Answers to Common Dental Questions
      </h1>

      <!-- Accent Divider -->
      <div class="w-20 h-[2px] bg-[#924f19]/30 mx-auto mb-6"></div>

      <p class="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
        Find clear and reliable answers to the most common dental concerns.
        If you still have questions, feel free to contact us.
      </p>
    </div>

    <!-- FAQ List -->
    <div class="space-y-5">
      <div
        v-for="(faq, index) in faqs"
        :key="index"
        class="bg-white rounded-2xl shadow-sm border border-gray-100
               hover:shadow-md transition-shadow duration-300 overflow-hidden"
      >
        <!-- Question -->
        <button
          @click="toggle(index)"
          class="w-full flex justify-between items-center
                 px-6 py-5 text-left"
        >
          <span class="text-gray-800 font-medium text-base md:text-lg">
            {{ faq.question }}
          </span>

          <span
            class="text-[#924f19] text-2xl font-light
                   transition-transform duration-300"
            :class="{ 'rotate-45': openIndex === index }"
          >
            +
          </span>
        </button>

        <!-- Answer -->
        <transition name="accordion">
          <div
            v-if="openIndex === index"
            class="px-6 pb-6 text-gray-600 leading-relaxed text-sm md:text-base"
          >
            {{ faq.answer }}
          </div>
        </transition>
      </div>
    </div>

    <!-- CTA -->
    <div class="text-center mt-20">
      <router-link
        to="/book-appointment-dental-clinic-guwahati"
        class="inline-block bg-teal-600 text-white
               px-10 py-4 rounded-full text-lg
               shadow-md hover:bg-teal-700 hover:shadow-lg
               transition-all duration-300"
      >
        Book an Appointment
      </router-link>
    </div>

  </div>
</section>
</template>

<script>
import { useHead } from "@vueuse/head";

export default {
  name: "FaqView",
  setup() {
    useHead({
      title: "Dental FAQs | Common Dental Questions Answered | Magnum Dental Guwahati",
      meta: [
        {
          name: "description",
          content:
            "Get answers to common dental questions at Magnum Dental Clinic Guwahati — about root canal, implants, braces, teeth cleaning, kids dentistry, and more.",
        },
        {
          name: "robots",
          content: "index, follow",
        },
        {
          property: "og:title",
          content: "Dental FAQs | Magnum Dental Clinic Guwahati",
        },
        {
          property: "og:url",
          content: "https://www.magnumdentalguwahati.in/faq-dental-clinic-in-assam",
        },
      ],
      link: [
        { rel: "canonical", href: "https://www.magnumdentalguwahati.in/faq-dental-clinic-in-assam" },
      ],
    });
  },
  data() {
    return {
      openIndex: null,
      faqs: [
              {
                question: "What dental services are available at Magnum Dental Clinic?",
                answer:
                  "At Magnum Dental Clinic, we offer complete dental care including check-ups and digital X-rays, fillings, root canal treatments, teeth cleaning and whitening, extractions, braces and aligners, crowns and bridges, veneers, dentures, pediatric dentistry, dental implants, gum treatments and surgery, as well as emergency dental care—all under one roof.",
              },
              {
                question: "Where is Magnum Dental Clinic located?",
                answer:
                  "Magnum Dental Clinic is located at First Floor, Global Tower, Pathar Quarry, Near Mangalam Grand, VIP Road, Guwahati.",
              },
              {
                question: "Is car parking available at the clinic?",
                answer:
                  "Yes, car parking facilities are available for the convenience of our patients.",
              },
              {
                question: "Do I need an appointment or can I walk in?",
                answer:
                  "You can directly walk in to Magnum Dental Clinic, VIP Road, Guwahati any day between 9:30 AM and 7:30 PM, or call us at 8638940021 to book an appointment at your convenience.",
              },
              {
                question: "Do you provide digital X-rays at the clinic?",
                answer:
                  "Yes, we offer digital dental X-rays for accurate diagnosis with minimal radiation exposure.",
              },
              {
                question: "Is dental treatment painful?",
                answer:
                  "At Magnum Dental Clinic, Guwahati, most dental treatments are carried out using advanced techniques and modern equipment to ensure maximum patient comfort. Local anesthesia is administered whenever necessary, allowing procedures to be performed with minimal to no pain. Patient comfort and safety are always our top priority.",
              },
              {
                question: "What safety and sterilisation measures do you follow at the clinic?",
                answer:
                  "At Magnum Dental Clinic, Guwahati, we give high priority to strict sterilisation and infection control to ensure complete safety for our patients. Our clinic uses a B-Class autoclave for advanced instrument sterilisation, along with single-use disposable materials wherever required, and follows internationally recommended infection control protocols at all times.",
              },
              {
                question: "Do you treat children at your clinic?",
                answer:
                  "Yes, we provide gentle and friendly dental care for children of all ages. We focus on creating a calm and comfortable environment to help children feel at ease during treatment.",
              },
              {
                question: "Do you provide braces and aligners (Orthodontic Treatments)?",
                answer:
                  "Yes, we provide braces and aligner treatments performed by an Orthodontist to correct misaligned teeth and bite issues. Each treatment plan is customized based on individual needs to improve both dental function and smile aesthetics.",
              },
              {
                question: "What is the right age for an orthodontic consultation?",
                answer:
                  "An orthodontic consultation is recommended around the age of 7, as early evaluation helps identify developing alignment or bite problems. However, orthodontic treatment can be successfully done at any age, including for adults.",
              },
              {
                question: "Do you provide wisdom tooth removal?",
                answer:
                  "Yes, wisdom tooth removal at our clinic is performed by a specialist using advanced techniques to ensure safety, comfort, and optimal healing.",
              },
              {
                question: "Can cosmetic dentistry improve my smile?",
                answer:
                  "Absolutely. Cosmetic dentistry enhances tooth color, shape, alignment, and overall smile confidence.",
              },
              {
                question: "Do you provide teeth whitening treatments?",
                answer:
                  "Yes, we offer safe and effective professional teeth whitening procedures.",
              },
              {
                question: "When is a root canal treatment (RCT) required?",
                answer:
                  "A root canal treatment is needed when the tooth pulp becomes infected or damaged due to deep decay or trauma.",
              },
              {
                question: "Is root canal treatment painful?",
                answer:
                  "With modern anesthesia, root canal treatment is usually painless and comfortable.",
              },
              {
                question: "How many sittings are needed for a root canal?",
                answer:
                  "Most root canal treatments are completed in one to two sittings, depending on the condition of the tooth.",
              },
              {
                question: "What should I do if I have sudden tooth pain?",
                answer:
                  "Contact us immediately for evaluation and pain relief. Avoid self-medication without consultation.",
              },
              {
                question: "Do you offer dental implants?",
                answer:
                  "Yes, we offer dental implant treatments to replace missing teeth with a strong, natural-looking, and long-lasting solution. Dental implants help restore proper chewing function, speech, and smile aesthetics, and are planned carefully to suit each patient’s individual needs.",
              },
              {
                question: "Do you provide dental crowns and veneers?",
                answer:
                  "Yes, we provide dental crowns and veneers to restore and enhance the appearance and function of teeth. Crowns help strengthen damaged or treated teeth, while veneers are ideal for improving tooth shape, color, and minor alignment issues, giving a natural and confident smile.",
              },
              {
                question: "Do you offer same-day pain relief treatment?",
                answer:
                  "Yes, we prioritize emergency pain management whenever possible.",
              },
            ],
    };
  },
  methods: {
    toggle(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
  },
};
</script>

<style scoped>
/* Accordion animation */
  .accordion-enter-active,
  .accordion-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .accordion-enter-from {
    opacity: 0;
    transform: translateY(-4px);
  }

  .accordion-enter-to {
    opacity: 1;
    transform: translateY(0);
  }

  .accordion-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  .accordion-leave-to {
    opacity: 0;
    transform: translateY(-4px);
  }
</style>
